/**
 * User helper.
 * @module helpers/AuthToken
 */
import intersection from 'lodash/intersection';

/**
 * Checks if the user has one or more given roles.
 * @method userHasRoles
 * @returns {boolean}
 */
export function userHasRoles(user, roles = []) {
  return intersection(user?.roles ?? [], roles).length > 0;
}

/**
 * Checks if the user is Manager.
 * @method isManager
 * @returns {boolean}
 */
export function isManager(user) {
  return userHasRoles(user, ['Manager']);
}

/**
 * Checks if the user can assign group.
 * @method canAssignGroup
 * @returns {boolean}
 */
export function canAssignGroup(isManager, group) {
  if (isManager) return true;
  return !group.roles.includes('Manager');
}

/**
 * Checks if the user can assign role.
 * @method canAssignRole
 * @returns {boolean}
 *
 * Customização GovBR:
 * - Manager: Só pode ser atribuído por usuários Manager
 * - Parameter Manager: Só pode ser atribuído por usuários Manager ou Parameter Manager
 *
 * @param {boolean|object} isManagerOrUser - Se boolean, indica se é Manager.
 *                                            Se object, é o objeto user com roles.
 * @param {object} role - Objeto role com id
 */
export function canAssignRole(isManagerOrUser, role) {
  // Mantém compatibilidade com assinatura antiga (boolean)
  const isManager =
    typeof isManagerOrUser === 'boolean'
      ? isManagerOrUser
      : userHasRoles(isManagerOrUser, ['Manager']);

  // Manager pode atribuir qualquer role
  if (isManager) return true;

  // Bloqueia atribuição de Manager para não-managers
  if (role.id === 'Manager') return false;

  // Customização: Bloqueia atribuição de Parameter Manager exceto para Manager ou Parameter Manager
  if (role.id === 'Parameter Manager') {
    // Se recebeu um objeto user, verifica se tem role Parameter Manager
    if (typeof isManagerOrUser === 'object') {
      return userHasRoles(isManagerOrUser, ['Parameter Manager']);
    }
    // Se recebeu boolean, bloqueia (já verificamos que não é Manager acima)
    return false;
  }

  // Outros roles podem ser atribuídos
  return true;
}
